/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {
  Config,
  Environment,
  Environments,
  getDeployedEnvironment,
  loadConfig,
  loadExternalConfig
} from '@ion/core/src/environment/environment';
import {error} from '@ion/core/src/logger/logger';
import {IApplicationFeatures} from '@ion/core/src/security/ApplicationFeatures';
import {getParameter} from '@ion/core/src/utils/QueryStringUtils';
import {setIsPermitted} from './store';

/**
 * Defines the query parameter which determines if the application is to use
 * an external environment for all API calls.
 *
 * The presence of an `?environment=dev|qa|stg|us|eu` query parameter will
 * result in the `environment.json` document for the given environment to be
 * loaded from the remote domain.
 */
export const ENVIRONMENT_QUERY = 'environment';

/**
 * Encapsulates the environment configuration details including the host
 * environment, remote environment, and whether the configuration was loaded
 * from an external domain.
 *
 * @see {@link getEnvironmentDetails}
 * @see {@link setEnvironmentDetails}
 */
export type EnvironmentDetails = {
  host: keyof Environment | string,
  remote: keyof Environment | string,
  isExternal: boolean;
  parameter: string;
}

/**
 * Indicates whether the environment configuration was loaded from an external
 * domain via the `environment` query parameter, or from the local host domain.
 *
 * @see  {@link getEnvironmentDetails}
 * @see  {@link EnvironmentDetails}
 */
let _details: Partial<EnvironmentDetails> = {
  isExternal : false
};

/**
 * Returns the environment configuration details including the external
 * environment, host environment, and whether the configuration was loaded
 * from an external domain.
 *
 * ```typescript
 *  import {getEnvironmentDetails} from '@ion/applications/src/env/env';
 *  ...
 *
 *  const details = getEnvironmentDetails();
 *  // details: {host: 'QA', remote: 'US', isExternal: true}
 * ```
 *
 * @see     {@link _details}
 * @see     {@link EnvironmentDetails}
 * @returns The environment details object containing external environment,
 *          host environment, and external configuration flag.
 */
export const getEnvironmentDetails = (): EnvironmentDetails => (
  _details as EnvironmentDetails
);

/**
 * Updates the environment configuration details with the given remote
 * environment, capturing the host environment and external configuration
 * flag. This function is invoked when loading an external environment
 * configuration from a remote domain.
 *
 * ```typescript
 *  import {setEnvironmentDetails} from '@ion/applications/src/env/env';
 *  ...
 *
 *  setEnvironmentDetails('qa');
 *  const details = getEnvironmentDetails();
 *  // details: {host: 'DEV', remote: 'QA', isExternal: true}
 * ```
 *
 * @see     {@link getEnvironmentDetails}
 * @see     {@link EnvironmentDetails}
 * @param   environment The remote environment to set as the configuration
 *          source.
 */
const setEnvironmentDetails = (environment: keyof Environment, parameter: string) => {
  const host = getDeployedEnvironment() as unknown as keyof Environment;
  const format = (val: keyof Environment) => val.toString().toUpperCase();
  _details = {
    host       : format(host),
    remote     : format(environment),
    isExternal : !!environment,
    parameter
  };
};

/**
 * Determines if the environment configuration was loaded from an external
 * environment which differs from the environment to which the application has
 * been deployed, for example, when the `?environment=us` query parameter has
 * been provided in the STG environment. Loading the configuration of the same
 * environment, or falling back to the local configuration when the external
 * configuration fails to load, is not considered cross environment.
 *
 * ```typescript
 *  import {isCrossEnvironment} from '@ion/applications/src/env/env';
 *  ...
 *
 *  // https://stg.admin.calamp.com/login?environment=us
 *  const crossEnvironment = isCrossEnvironment(); // true
 * ```
 *
 * @see     {@link getEnvironmentDetails}
 * @returns `true` if the configuration was loaded from an external environment
 *          which differs from the deployed environment, otherwise `false`.
 */
export const isCrossEnvironment = (): boolean => {
  const {isExternal, host, remote} = getEnvironmentDetails();
  return !!isExternal && host !== remote;
};

/**
 * Reloads the environment configuration from the default domain, removing the
 * `environment` query parameter from the URL and setting the path to `/login`.
 * This function is to be invoked by components which inform the user that they
 * are using the application in a specific environment but pointing to another
 * environment, thus allowing for aborting prior to proceeding in the event they
 * accidentally provided the environment query string parameter.
 *
 * ```typescript
 *   import {reloadDefault} from '@ion/applications/src/env/env';
 *   ...
 *
 *   reloadDefault();
 *   reloadDefault('environment', '/dashboard');
 * ```
 *
 * @param   param The query parameter name to remove from the URL. Defaults to
 *          `ENVIRONMENT_QUERY`.
 * @param   route The path to navigate to after removing the query parameter.
 *          Defaults to `/login`.
 */
export const reloadDefault = (param = ENVIRONMENT_QUERY, route = '/login') => {
  const url = new URL(window.location.href);
  url.searchParams.delete(param);
  url.pathname = route;
  window.location.assign(url.toString());
};

/**
 * Determines if the provided `user` is permitted to access cross environment
 * services based on the list of 'enhancedCapabilitiesRoleIds' and the user's
 * assigned `Role` ids.
 *
 * If the application does not define 'enhancedCapabilitiesRoleIds' in it's
 * extended attributes, then the assumption is that there are no restrictions
 * and the cross-environment features will be enabled by default.
 *
 * This function should be invoked upon application initialization immediately
 * after the application's `Features` API has been initialized. If the application
 * does not provide an `enhancedCapabilitiesRoleIds` entry, this function will
 * return `undefined, otherwise, if provided, it will return `true` if the user
 * has an enhanced capabilities role assigned, otherwise, `false` if not assigned.
 *
 * Clients can assume that a returned value of `undefined` indicates that the
 * application does not have enhanced capabilities enabled, and therefore the
 * feature can be ignored completely.
 *
 * ```typescript
 *  // initialize Features API (typically in store/actions/initialization.ts)
 *  Features.initialize({
 *    user,
 *    application
 *    ...
 *  });
 *  // invoke verifyAccess with reference to Features API ...
 *  const isPermitted = verifyCrossEnvironmentAccess(Features);
 *  if (isPermitted !== false) {
 *    dispatch(initializationComplete({accounts, role, user}, isFromLegacyAdmin, route));
 *  }
 * ```
 *
 * @see {@link IApplicationFeatures.hasEnhancedCapabilitiesRole}
 */
export const verifyCrossEnvironmentAccess = (features: IApplicationFeatures): boolean | undefined => {
  if (getEnvironmentDetails().isExternal) {
    const hasEnhancedCapabilitiesRole = features.hasEnhancedCapabilitiesRole();
    if (hasEnhancedCapabilitiesRole !== false) {
      setIsPermitted(true);
    } else {
      setIsPermitted(false);
    }
    return hasEnhancedCapabilitiesRole;
  }
};

/**
 * Loads the environment configuration for the application, defaulting to loading
 * the `environment.json` document from the host domain. If the `domains` argument
 * is provided and a valid `environment` query string parameter is included, the
 * value of the `environment` query string parameter will be used to map to the
 * predefined `domain` mappings for the given environment.
 *
 * If the `environment.json` document for the given remote domain fails to load,
 * by default, this function will fallback to loading the `environment.json` document
 * from root domain. This behavior can be overridden by setting the `useFallback`
 * argument to `false`.
 *
 * Applications are to implement a `src/env/env.ts` module as an abstraction which
 * defines the mapped domains and invokes the `loadEnvironmentConfig` function:
 *
 * ```typescript
 *   // packages/ion-some-app/src/env/env.ts
 *
 *   const domains: Partial<Environments> = {
 *     [Environment.dev] : 'https://dev.admin.calamp.com',
 *     [Environment.qa]  : 'https://qa.admin.calamp.com',
 *     [Environment.stg] : 'https://stg.admin.calamp.com',
 *     [Environment.eu]  : 'https://admin.calamp.co.uk',
 *     [Environment.us]  : 'https://admin.calamp.com'
 *   };
 *
 *   // implement application level loadConfig and invoke from _app.tsx
 *   export const loadConfig = async(): Promise<Config> => (
 *     await loadEnvironmentConfig(domains)
 *   );
 * ```
 *
 * Then, in the applications `_app.tsx`, import and invoke the application specific
 * `loadConfig` implementation rather than the default `@ion/core/src/environment`
 * implementation:
 *
 * ```typescript
 *   import {
 *     Config,
 *     Environment,
 *     Environments
 *   from '@ion/core/src/environment/environment';
 *
 *   const domains: Partial<Environments> = {
 *     [Environment.dev] : 'https://dev.admin.calamp.com',
 *     [Environment.qa]  : 'https://qa.admin.calamp.com',
 *     [Environment.stg] : 'https://stg.admin.calamp.com',
 *     [Environment.eu]  : 'https://admin.calamp.co.uk',
 *     [Environment.us]  : 'https://admin.calamp.com'
 *   };
 *
 *   // implement application level loadConfig and invoke from _app.tsx
 *   export const loadConfig = async(): Promise<Config> => (
 *     await loadEnvironmentConfig(domains) as unknown as Config
 *   );
 * ```
 *
 * @see     {@link getEnvironmentDetails}
 * @returns A promise that resolves with the parsed JSON content (configuration) of
 *          the `environment.json` document from the specified environment.
 */
const loadEnvironmentConfig = async(
  domains: Partial<Environments>,
  param = ENVIRONMENT_QUERY,
  useFallback = true,
  noCache = true
): Promise<Config | null | undefined> => { // eslint-disable-line max-params
  const environment = domains ? getParameter(param) : null;
  if (environment) {
    try {
      const config: Config = await loadExternalConfig(domains, environment) as Config;
      if (config) {
        setEnvironmentDetails(environment, param);
        return config;
      }
    } catch (err) {
      error(`Unable to load environment.json from ${environment}, falling back to local environment.json`, err);
    }
  }
  return useFallback ? await loadConfig(undefined, noCache) : null;
};
export default loadEnvironmentConfig;
